import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  BookOpen, 
  Plus,
  Eye,
  ArrowRight,
  Calendar,
  CheckCircle,
  Clock,
  AlertTriangle,
  Archive
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  draft: { label: '下書き', color: 'badge-neutral', icon: Clock },
  pending_explanation: { label: '説明待ち', color: 'badge-warning', icon: Clock },
  pending_consent: { label: '同意待ち', color: 'badge-warning', icon: AlertTriangle },
  active: { label: '有効', color: 'badge-success', icon: CheckCircle },
  monitoring: { label: 'モニタリング中', color: 'badge-success', icon: Eye },
  completed: { label: '完了', color: 'badge-neutral', icon: CheckCircle },
  archived: { label: 'アーカイブ', color: 'badge-neutral', icon: Archive }
};

export default function SupportPlansPage() {
  const [plans, setPlans] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    client_id: '',
    status: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [plansRes, clientsRes] = await Promise.all([
        axios.get(`${API}/support-plans`),
        axios.get(`${API}/clients`)
      ]);
      setPlans(plansRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      console.error('データの取得に失敗しました', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    const actualValue = value === 'all' ? '' : value;
    setFilters(prev => ({ ...prev, [field]: actualValue }));
  };

  const filteredPlans = plans.filter(plan => {
    if (filters.client_id && plan.client_id !== filters.client_id) return false;
    if (filters.status && plan.status !== filters.status) return false;
    return true;
  });

  // ステータス別の統計
  const statusCounts = plans.reduce((acc, plan) => {
    acc[plan.status] = (acc[plan.status] || 0) + 1;
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="support-plans-page">
      {/* ヘッダー */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">
            個別支援計画
          </h1>
          <p className="text-muted-foreground mt-1">
            計画数: {plans.length}件
          </p>
        </div>
        <Link to="/support-plans/new">
          <Button className="btn-active" data-testid="add-plan-button">
            <Plus size={18} className="mr-2" />
            新規計画作成
          </Button>
        </Link>
      </div>

      {/* ステータス統計 */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {Object.entries(STATUS_CONFIG).map(([key, config]) => {
          const Icon = config.icon;
          return (
            <Card 
              key={key} 
              className={`card-surface cursor-pointer hover:border-primary/50 transition-colors ${filters.status === key ? 'border-primary' : ''}`}
              onClick={() => handleFilterChange('status', filters.status === key ? 'all' : key)}
            >
              <CardContent className="p-3 text-center">
                <Icon size={20} className="mx-auto mb-1 text-muted-foreground" />
                <p className="text-xl font-semibold">{statusCounts[key] || 0}</p>
                <p className="text-xs text-muted-foreground">{config.label}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* フィルター */}
      <Card className="card-surface">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Select 
              value={filters.client_id || 'all'} 
              onValueChange={(value) => handleFilterChange('client_id', value)}
            >
              <SelectTrigger data-testid="filter-client-select">
                <SelectValue placeholder="利用者で絞り込み" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">すべての利用者</SelectItem>
                {clients.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select 
              value={filters.status || 'all'} 
              onValueChange={(value) => handleFilterChange('status', value)}
            >
              <SelectTrigger data-testid="filter-status-select">
                <SelectValue placeholder="ステータスで絞り込み" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">すべてのステータス</SelectItem>
                {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                  <SelectItem key={key} value={key}>{config.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button 
              variant="outline"
              onClick={() => setFilters({ client_id: '', status: '' })}
              data-testid="clear-filters-button"
            >
              フィルターをクリア
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* 計画一覧 */}
      {filteredPlans.length === 0 ? (
        <Card className="card-surface">
          <CardContent className="py-12 text-center">
            <BookOpen size={48} className="mx-auto mb-4 text-muted-foreground/50" />
            <p className="text-muted-foreground">
              {plans.length === 0 ? '個別支援計画がありません' : '条件に一致する計画がありません'}
            </p>
            <Link to="/support-plans/new">
              <Button className="mt-4" variant="outline">
                最初の計画を作成する
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <Card className="card-surface overflow-hidden">
          <div className="table-container">
            <Table>
              <TableHeader className="table-header">
                <TableRow>
                  <TableHead className="table-th">利用者</TableHead>
                  <TableHead className="table-th">サービス</TableHead>
                  <TableHead className="table-th">計画期間</TableHead>
                  <TableHead className="table-th">ステータス</TableHead>
                  <TableHead className="table-th">作成者</TableHead>
                  <TableHead className="table-th">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPlans.map((plan) => {
                  const statusConfig = STATUS_CONFIG[plan.status] || STATUS_CONFIG.draft;
                  return (
                    <TableRow 
                      key={plan.id} 
                      className="table-row-hover"
                      data-testid={`plan-row-${plan.id}`}
                    >
                      <TableCell className="table-td">
                        <div>
                          <p className="font-medium">{plan.client_name}</p>
                          <p className="text-xs text-muted-foreground">v{plan.version}</p>
                        </div>
                      </TableCell>
                      <TableCell className="table-td">
                        <span className={
                          plan.service_type === '日中一時支援' ? 'badge-success' :
                          plan.service_type === '移動支援' ? 'badge-warning' : 'badge-neutral'
                        }>
                          {plan.service_type}
                        </span>
                      </TableCell>
                      <TableCell className="table-td">
                        <div className="flex items-center gap-2">
                          <Calendar size={14} className="text-muted-foreground" />
                          <span className="text-sm">
                            {plan.plan_period_start} 〜 {plan.plan_period_end}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="table-td">
                        <span className={statusConfig.color}>
                          {statusConfig.label}
                        </span>
                      </TableCell>
                      <TableCell className="table-td text-sm">
                        {plan.created_by_name}
                      </TableCell>
                      <TableCell className="table-td">
                        <Link to={`/support-plans/${plan.id}`}>
                          <Button 
                            size="sm" 
                            variant="outline"
                            data-testid={`view-plan-${plan.id}`}
                          >
                            詳細 <ArrowRight size={14} className="ml-1" />
                          </Button>
                        </Link>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}
